import { NgModule } from '@angular/core';
import { RouterModule, Routes } from '@angular/router';
import { AppResultRedirectGuard } from './core/guards/app-result-redirect.guard';
import { AuthGuard } from './core/guards/auth.guard';
import { SkinnyBsshGuard } from './core/guards/skinnyBssh.guard';
import { RedirectPathResolverService } from './core/services/resolver/redirect-path-resolver.service';
import { SearchPathResolverService } from './core/services/resolver/search-path-resolver.service';
import { Constants } from './core/utilities/constants';
import { HttpErrorComponent } from './shared/http-error/http-error.component';
import { SwitchWorkgroupModalComponent } from './shared/modals/switch-workgroup/switch-workgroup-modal.component';
import { ResourceDeletedComponent } from './shared/resource-deleted/resource-deleted.component';
import { UserStoreReadyResolver } from './run-planning/resolver/user-store-ready.resolver';
import { OauthModalComponent } from './shared/modals/oauth-modal/oauth-modal.component';
import { ReloadOauthGuard } from './core/guards/reload-oauth.guard';
import { OAuthDeviceModalComponent } from './shared/modals/oauth-device-modal/oauth-device-modal.component';

export const routes: Routes = [
  {
    path: 'runs',
    loadChildren: () => import('./runs/runs.module').then(m => m.RunsModule),
    canLoad: [AuthGuard, SkinnyBsshGuard],
    data: {
      pageTitlePrefix: 'Runs'
    }
  },
  {
    path: 'run',
    loadChildren: () => import('./runs/run-details.module').then(m => m.RunDetailsModule),
    canLoad: [AuthGuard, SkinnyBsshGuard],
    data: {
      pageTitlePrefix: 'Runs'
    }
  },
  {
    path: 'settings',
    loadChildren: () => import('./settings/settings.module').then(m => m.SettingsModule),
    canLoad: [AuthGuard, SkinnyBsshGuard],
    data: {
      pageTitlePrefix: 'Settings'
    }
  },
  {
    path: 'resources',
    loadChildren: () => import('./resources/resources.module').then(m => m.ResourcesModule),
    canLoad: [AuthGuard, SkinnyBsshGuard],
    data: {
      pageTitlePrefix: 'Resources'
    }
  },
  {
    path: 'biosamples',
    loadChildren: () => import('./biosamples/biosamples.module').then(m => m.BiosamplesModule),
    canLoad: [AuthGuard, SkinnyBsshGuard],
    data: {
      pageTitlePrefix: 'Biosamples'
    }
  },
  {
    path: 'project',
    redirectTo: 'projects'
  },
  {
    path: 'projects',
    loadChildren: () => import('./projects/projects.module').then(m => m.ProjectsModule),
    canLoad: [AuthGuard, SkinnyBsshGuard],
    data: {
      pageTitlePrefix: 'Projects'
    }
  },
 {
    path: 'apps',
    loadChildren: () => import('./apps/apps.module').then(m => m.AppsModule),
    canLoad: [AuthGuard]
  },
  {
    path: 'agreements',
    loadChildren: () => import('./agreements/agreements.module').then(m => m.AgreementsModule),
    canLoad: []
  },
  {
    path: 'datacentral',
    loadChildren: () => import('./demo-data/demo-data.module').then(m => m.DemoDataModule),
    canLoad: [SkinnyBsshGuard],
    data: {
      pageTitlePrefix: 'Demo Data'
    }
  },
  {
    path: 'analyses',
    loadChildren: () => import('./analyses/analyses.module').then(m => m.AnalysesModule),
    canLoad: [AuthGuard],
    data: {
      pageTitlePrefix: 'Analyses'
    }
  },
  {
    path: 'analyses',
    loadChildren: () => import('./analyses/analysis-details.module').then(m => m.AnalysisDetailsModule),
    canLoad: [AuthGuard, SkinnyBsshGuard],
    data: {
      pageTitlePrefix: 'Analyses'
    }
  },
  {
    path: 'appresults/:appResultId',
    canLoad: [AuthGuard],
    canActivate: [AppResultRedirectGuard],
    // This is mostly a placeholder component that we should theoretically never hit from here. We can potentially get this component displayed from the guard though.
    component: HttpErrorComponent,
    data: {
      isTitleDynamic: true,
      pageTitlePrefix: 'Error'
    }
  },
  {
    path: 'dashboard',
    loadChildren: () => import('./dashboard/dashboard.module').then(m => m.DashboardModule),
    canLoad: [AuthGuard, SkinnyBsshGuard],
    data: {
      pageTitlePrefix: 'Dashboard'
    }
  },
  {
    path: `s/:${Constants.InviteToken}`,
    loadChildren: () => import('./dashboard/dashboard.module').then(m => m.DashboardModule),
    canLoad: [AuthGuard],
    data: {
      pageTitlePrefix: 'Dashboard'
    }
  },
  {
    path: `g/:${Constants.InviteToken}`,
    loadChildren: () => import('./dashboard/dashboard.module').then(m => m.DashboardModule),
    canLoad: [AuthGuard],
    data: {
      pageTitlePrefix: 'Dashboard'
    }
  },
  {
    path: `sample`,
    loadChildren: () => import('./samples/sample-details.module').then(m => m.SampleDetailsModule),
    canLoad: [AuthGuard],
    data: {
      pageTitlePrefix: 'Sample Details'
    }
  },
  // Enable when pools pages are ready
  {
    path: `pools`,
    loadChildren: () => import('./pools/pool-details.module').then(m => m.PoolDetailsModule),
    canLoad: [AuthGuard],
    data: {
      pageTitlePrefix: 'Pool Details'
    }
  },
  {
    path: 'cloud-run-prep',
    loadChildren: () => import('./cloud-run-prep/cloud-run-prep.module').then(m => m.CloudRunPrepModule),
    canLoad: [AuthGuard],
    data: {
      pageTitlePrefix: 'Runs'
    }
  },
  {
    path: 'run-planning',
    canLoad: [AuthGuard],
    loadChildren: () => import('./run-planning/run-planning.module').then(m => m.RunPlanningModule),
    resolve: {
      userStoreReady: UserStoreReadyResolver
    },
    data: {
      pageTitlePrefix: 'Run Setup'
    }
  },
  {
    path: '',
    redirectTo: '/dashboard',
    pathMatch: 'full'
  },
  {
    path: 'search',
    loadChildren: () => import('./search/search.module').then(m => m.SearchModule),
    canLoad: [AuthGuard],
    data: {
      pageTitlePrefix: 'Search'
    }
  },
  {
    path: Constants.RESOURCE_DELETED,
    component: ResourceDeletedComponent,
    data: {
      pageTitlePrefix: 'Page Gone'
    }
  },
  {
    path: Constants.SWITCH_WORKGROUP_ROUTE,
    component: SwitchWorkgroupModalComponent,
    canActivate: [AuthGuard],
    data: {
      pageTitlePrefix: 'Switch Workgroup'
    }
  },
  {
    path: Constants.OAUTH_AUTHORIZE_ROUTE,
    component: OauthModalComponent,
    canActivate: [AuthGuard, ReloadOauthGuard],
    data: {
      pageTitlePrefix: 'Oauth Authorize',

    }
  },
  {
    path: Constants.OAUTH_DEVICE_ROUTE,
    component: OAuthDeviceModalComponent,
    canActivate: [AuthGuard],
    data: {
      pageTitlePrefix: 'Oauth Device'
    }
  },
  {
    path: Constants.HTTP_ERROR_ROUTE,
    component: HttpErrorComponent,
    canActivate: [AuthGuard],
    data: {
      isTitleDynamic: true,
      pageTitlePrefix: 'Error'
    }
  },
  {
    path: '**',
    component: HttpErrorComponent,
    resolve: {
      undefinedPath: RedirectPathResolverService
    },
    runGuardsAndResolvers: 'always',
    data: {
      isTitleDynamic: true,
      httpErrorStatus: 404
    }
  }
];

@NgModule({
  imports: [RouterModule.forRoot(routes, {
    enableTracing: false,

    // Send user scrollbar to the top on route change.
    scrollPositionRestoration: 'top'
  })],
  exports: [RouterModule],
  providers: [
    SearchPathResolverService,
    RedirectPathResolverService
  ]
})
export class AppRoutingModule { }
